export enum Unit {
  AMPOULES = "AMPOULES",
  APPLICATORS = "APPLICATORS",
  BOTTLES = "BOTTLES",
  BOXES = "BOXES",
  CAPSULES = "CAPSULES",
  CUPS = "CUPS",
  DOSES = "DOSES",
  G_PER_ML = "G_PER_ML",
  GRAMS = "GRAMS",
  IU = "IU",
  IU_PER_ML = "IU_PER_ML",
  OZ = "OUNCES",
  FLUID_OZ = "FLUID_OUNCES",
  KILOGRAMS = "KILOGRAMS",
  KITS = "KITS",
  LITERS = "LITERS",
  MCG = "MCG",
  MG = "MG",
  MG_PER_ML = "MG_PER_ML",
  ML = "ML",
  MMOL = "MMOL",
  MICROGRAM_PER_ML = "MICROGRAM_PER_ML",
  PACKS = "PACKS",
  PAIRS = "PAIRS",
  PATCHES = "PATCHES",
  PERCENTAGE = "PERCENTAGE",
  PIECES = "PIECES",
  POUCHES = "POUCHES",
  ROLLS = "ROLLS",
  SACHETS = "SACHETS",
  SHEETS = "SHEETS",
  SPRAYS = "SPRAYS",
  STRIPS = "STRIPS",
  SYRINGES = "SYRINGES",
  TABLETS = "TABLETS",
  TABLETS_PER_DAY = "TABLETS_PER_DAY",
  TESTS = "TESTS",
  TROCHES = "TROCHES",
  TUBES = "TUBES",
  UNITS = "UNITS",
  VIALS = "VIALS",
  // Aesthetic, medical spa and weight loss specific units
  TREATMENTS = "TREATMENTS",
  SESSIONS = "SESSIONS",
  PULSES = "PULSES",
  JOULES = "JOULES",
  CENTIMETERS = "CENTIMETERS",
  THREAD_COUNT = "THREAD_COUNT",
  INJECTIONS = "INJECTIONS",
  WRINKLE_UNITS = "WRINKLE_UNITS",
  // Imperial units for conversions
  POUNDS = "POUNDS",
  FEET = "FEET",
  INCHES = "INCHES",
}

// Unit conversion utility functions
export const UnitConversions = {
  kgToLbs: (weightInKg?: number | null): number | null => {
    if (weightInKg == null || isNaN(weightInKg)) return null;
    return weightInKg * 2.20462;
  },

  lbsToKg: (weightInLbs?: number | null): number | null => {
    if (weightInLbs == null || isNaN(weightInLbs)) return null;
    return weightInLbs / 2.20462;
  },

  cmToInches: (heightInCm?: number | null): number | null => {
    if (heightInCm == null || isNaN(heightInCm)) return null;
    return heightInCm / 2.54;
  },

  inchesToCm: (heightInInches?: number | null): number | null => {
    if (heightInInches == null || isNaN(heightInInches)) return null;
    return heightInInches * 2.54;
  },

  feetInchesToCm: (feet: number, inches: number): number => {
    const totalInches = feet * 12 + inches;
    return totalInches * 2.54;
  },

  cmToFeetInches: (heightInCm: number): { feet: number; inches: number } => {
    const totalInches = Math.round(heightInCm / 2.54);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return { feet, inches };
  },

  gToMg: (grams?: number | null): number | null => {
    if (!grams) return null;
    return grams * 1000;
  },

  mgToG: (milligrams?: number | null): number | null => {
    if (!milligrams) return null;
    return milligrams / 1000;
  },

  mgToMcg: (milligrams?: number | null): number | null => {
    if (!milligrams) return null;
    return milligrams * 1000;
  },

  mcgToMg: (micrograms?: number | null): number | null => {
    if (!micrograms) return null;
    return micrograms / 1000;
  },

  mlToLiters: (milliliters?: number | null): number | null => {
    if (!milliliters) return null;
    return milliliters / 1000;
  },

  litersToMl: (liters?: number | null): number | null => {
    if (!liters) return null;
    return liters * 1000;
  },

  celsiusToFahrenheit: (celsius?: number | null): number | null => {
    if (celsius == null || isNaN(celsius)) return null;
    return (celsius * 9) / 5 + 32;
  },

  fahrenheitToCelsius: (fahrenheit?: number | null): number | null => {
    if (fahrenheit == null || isNaN(fahrenheit)) return null;
    return ((fahrenheit - 32) * 5) / 9;
  },

  getConversionFactor: (fromUnit: Unit, toUnit: Unit): number | null => {
    // Weight conversions
    if (fromUnit === Unit.KILOGRAMS && toUnit === Unit.POUNDS) return 2.20462;
    if (fromUnit === Unit.POUNDS && toUnit === Unit.KILOGRAMS)
      return 1 / 2.20462;

    // Length conversions
    if (fromUnit === Unit.CENTIMETERS && toUnit === Unit.INCHES)
      return 1 / 2.54;
    if (fromUnit === Unit.INCHES && toUnit === Unit.CENTIMETERS) return 2.54;

    // Mass conversions
    if (fromUnit === Unit.GRAMS && toUnit === Unit.MG) return 1000;
    if (fromUnit === Unit.MG && toUnit === Unit.GRAMS) return 1 / 1000;
    if (fromUnit === Unit.MG && toUnit === Unit.MCG) return 1000;
    if (fromUnit === Unit.MCG && toUnit === Unit.MG) return 1 / 1000;

    // Volume conversions
    if (fromUnit === Unit.ML && toUnit === Unit.LITERS) return 1 / 1000;
    if (fromUnit === Unit.LITERS && toUnit === Unit.ML) return 1000;

    // Same unit
    if (fromUnit === toUnit) return 1.0;

    return null;
  },

  convert: (
    value: number | null,
    fromUnit: Unit,
    toUnit: Unit,
  ): number | null => {
    if (!value) return null;

    const conversionFactor = UnitConversions.getConversionFactor(
      fromUnit,
      toUnit,
    );
    if (conversionFactor === null) return null;

    return value * conversionFactor;
  },
};
